import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Dimensions, Platform, StyleSheet, View } from 'react-native';
import { Portal } from 'react-native-paper';
import Animated, {
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { fs, s, v } from '../../../utils/libs';
import { useTheme } from '../../../theme';
import Typography from '../Typography';

type HeaderTooltipPlacement = 'top' | 'bottom';

type ShowHeaderTooltipParams = {
  id: string;
  title: string;
  targetRef: React.RefObject<any>;
  placement?: HeaderTooltipPlacement;
  offset?: number;
};

type HeaderTooltipState = {
  id: string;
  title: string;
};

type HeaderTooltipLayout = {
  x: number;
  y: number;
  width: number;
  placement: HeaderTooltipPlacement;
};

type HeaderTooltipContextValue = {
  showTooltip: (params: ShowHeaderTooltipParams) => void;
  hideTooltip: (id: string, delayMs?: number) => void;
  hideActiveTooltip: () => void;
};

const HeaderTooltipContext = createContext<HeaderTooltipContextValue | null>(
  null,
);

export const useHeaderTooltip = () => useContext(HeaderTooltipContext);

export const HeaderTooltipProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { theme } = useTheme();
  const [tooltip, setTooltip] = useState<HeaderTooltipState | null>(null);
  const [layout, setLayout] = useState<HeaderTooltipLayout | null>(null);
  const [mounted, setMounted] = useState(false);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingTooltipIdRef = useRef<string | null>(null);
  const activeTooltipIdRef = useRef<string | null>(null);
  const mountedRef = useRef(false);
  const opacity = useSharedValue(0);
  const translateY = useSharedValue(-2);
  const scale = useSharedValue(0.96);

  const clearHideTimer = useCallback(() => {
    if (!hideTimerRef.current) return;
    clearTimeout(hideTimerRef.current);
    hideTimerRef.current = null;
  }, []);

  const clearShowTimer = useCallback(() => {
    if (!showTimerRef.current) return;
    clearTimeout(showTimerRef.current);
    showTimerRef.current = null;
  }, []);

  const clearTooltipState = useCallback(() => {
    mountedRef.current = false;
    activeTooltipIdRef.current = null;
    setMounted(false);
    setTooltip(null);
    setLayout(null);
  }, []);

  const hideActiveTooltip = useCallback(() => {
    clearHideTimer();
    clearShowTimer();
    pendingTooltipIdRef.current = null;
    opacity.value = withTiming(0, { duration: 0 });
    translateY.value = withTiming(-2, { duration: 0 });
    scale.value = withTiming(0.98, { duration: 0 });
    clearTooltipState();
  }, [
    clearHideTimer,
    clearShowTimer,
    clearTooltipState,
    opacity,
    scale,
    translateY,
  ]);

  const hideTooltip = useCallback(
    (id: string, delayMs = 90) => {
      clearHideTimer();
      clearShowTimer();
      if (pendingTooltipIdRef.current === id) {
        pendingTooltipIdRef.current = null;
      }

      hideTimerRef.current = setTimeout(() => {
        if (activeTooltipIdRef.current !== id) {
          hideTimerRef.current = null;
          return;
        }

        opacity.value = withTiming(0, { duration: 90 });
        translateY.value = withTiming(-2, { duration: 90 });
        scale.value = withTiming(0.98, { duration: 90 }, finished => {
          if (finished) {
            runOnJS(clearTooltipState)();
          }
        });
        hideTimerRef.current = null;
      }, delayMs);
    },
    [
      clearHideTimer,
      clearShowTimer,
      clearTooltipState,
      opacity,
      scale,
      translateY,
    ],
  );

  const showTooltip = useCallback(
    ({
      id,
      title,
      targetRef,
      placement = 'bottom',
      offset = v(8),
    }: ShowHeaderTooltipParams) => {
      if (!title?.trim()) {
        hideActiveTooltip();
        return;
      }
      clearHideTimer();
      clearShowTimer();
      pendingTooltipIdRef.current = id;

      const measureAndShow = () => {
        const target = targetRef.current;
        const measure = target?.measureInWindow;
        if (typeof measure !== 'function') {
          hideActiveTooltip();
          return;
        }

        measure((x: number, y: number, width: number, height: number) => {
          if (pendingTooltipIdRef.current !== id) return;

          const windowWidth = Dimensions.get('window').width;
          const tooltipWidth = Math.min(
            s(240),
            Math.max(s(96), title.trim().length * fs(7) + s(28)),
          );
          const centerX = x + width / 2;
          const left = Math.max(
            s(6),
            Math.min(
              centerX - tooltipWidth / 2,
              windowWidth - tooltipWidth - s(6),
            ),
          );
          const resolvedY =
            placement === 'top' ? y - offset - v(36) : y + height + offset;
          const wasMounted = mountedRef.current;

          activeTooltipIdRef.current = id;
          setTooltip({ id, title });
          setLayout({
            x: left,
            y: resolvedY,
            width: tooltipWidth,
            placement,
          });

          if (!wasMounted) {
            mountedRef.current = true;
            setMounted(true);
            opacity.value = 0;
            translateY.value = -2;
            scale.value = 0.96;
            opacity.value = withTiming(1, { duration: 110 });
            translateY.value = withTiming(0, { duration: 110 });
            scale.value = withTiming(1, { duration: 110 });
          }
        });
      };

      if (mountedRef.current) {
        measureAndShow();
        return;
      }

      showTimerRef.current = setTimeout(measureAndShow, 200);
    },
    [
      clearHideTimer,
      clearShowTimer,
      hideActiveTooltip,
      opacity,
      scale,
      translateY,
    ],
  );

  const contextValue = useMemo(
    () => ({
      showTooltip,
      hideTooltip,
      hideActiveTooltip,
    }),
    [hideActiveTooltip, hideTooltip, showTooltip],
  );

  const styles = useMemo(() => createStyles(theme), [theme]);
  const animatedTooltipStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
    transform: [{ translateY: translateY.value }, { scale: scale.value }],
  }));

  useEffect(
    () => () => {
      clearHideTimer();
      clearShowTimer();
    },
    [clearHideTimer, clearShowTimer],
  );

  return (
    <HeaderTooltipContext.Provider value={contextValue}>
      {children}
      <Portal>
        {mounted && tooltip && layout ? (
          <View pointerEvents="none" style={StyleSheet.absoluteFill}>
            <Animated.View
              style={[
                styles.tooltip,
                animatedTooltipStyle,
                {
                  left: layout.x,
                  top: layout.y,
                  width: layout.width,
                },
              ]}>
              <Typography numberOfLines={1} style={styles.tooltipText}>
                {tooltip.title}
              </Typography>
            </Animated.View>
          </View>
        ) : null}
      </Portal>
    </HeaderTooltipContext.Provider>
  );
};

const createStyles = (theme: any) =>
  StyleSheet.create({
    tooltip: {
      position: Platform.OS === 'web' ? ('fixed' as any) : 'absolute',
      maxWidth: s(240),
      minHeight: v(36),
      paddingHorizontal: s(12),
      paddingVertical: v(8),
      borderRadius: theme.borderRadius.md,
      backgroundColor: 'rgba(24, 24, 27, 0.96)',
      alignItems: 'center',
      justifyContent: 'center',
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 6 },
      shadowOpacity: 0.22,
      shadowRadius: 12,
      elevation: 8,
      zIndex: 9999,
    } as any,
    tooltipText: {
      color: '#fff',
      fontSize: fs(13),
      lineHeight: fs(17),
      fontWeight: '600',
      textAlign: 'center',
    },
  });

export type { HeaderTooltipPlacement };
